// SpecReconciliationGate — PM-facing gate that appears once Demo qa_passes.
// Until the PM marks the bound spec "still valid", the next phase stays
// locked. Captures clean deviation data when reality drifts from the spec.
//
// Trigger:   Demo phase status === "qa_passed" AND no spec_valid decision.
// Effect:    isPhaseUnlocked(plumbing) === false until reconciled.
// Constraint: No change-order flow, no spec swap, no notifications.

import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ShieldQuestion,
  CheckCircle2,
  AlertTriangle,
  Lock,
  CheckCheck,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAppData } from "@/data/AppDataContext";
import { resolveBoundSpec, type BoundJobSpec } from "@/data/jobSpec";
import {
  reconcilePhase,
  isReconciliationPending,
  DEVIATION_REASONS,
  type DeviationReason,
  type ReconciliationDecision,
} from "@/data/jobSpecExecution";

interface Props {
  jobId: string;
  bound?: BoundJobSpec;
}

const OTHER_MIN = 20;

export function SpecReconciliationGate({ jobId, bound }: Props) {
  const { specExecutions, setSpecExecutions } = useAppData();
  const spec = useMemo(() => resolveBoundSpec(bound), [bound]);
  const execution = useMemo(
    () => specExecutions.find((e) => e.jobId === jobId),
    [specExecutions, jobId],
  );

  const demoPhase = execution?.phases.find((p) => p.phaseName === "demo");

  const [decision, setDecision] = useState<ReconciliationDecision | "">("");
  const [reason, setReason] = useState<DeviationReason | "">("");
  const [notes, setNotes] = useState("");
  const [pmName, setPmName] = useState("");

  if (!bound || !spec || !execution || !demoPhase) return null;

  // Spec already reconciled clean — show a compact passed banner.
  if (demoPhase.reconciliation?.decision === "spec_valid") {
    const r = demoPhase.reconciliation;
    return (
      <Card className="shadow-none border-success/30">
        <CardContent className="p-3 flex items-center gap-3">
          <CheckCheck className="h-4 w-4 text-success shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-success">
              Demo Reconciled — Spec Still Valid
            </p>
            <p className="text-[11px] text-muted-foreground">
              Cleared by {r.decidedBy} · {new Date(r.decidedAt).toLocaleString()}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Demo is parked on a deviation — show the deviation record.
  const parkedOnDeviation =
    demoPhase.reconciliation?.decision === "deviation_required";

  // Gate appears only when Demo is qa_passed and not yet cleared.
  const pending = isReconciliationPending(demoPhase);

  if (!pending && !parkedOnDeviation) {
    // Demo hasn't reached qa_passed yet — gate is dormant. Show nothing
    // (the execution panel already communicates the demo state).
    return null;
  }

  const handleSubmit = () => {
    if (!decision) {
      toast.error("Choose a reconciliation decision.");
      return;
    }
    setSpecExecutions((prev) =>
      prev.map((e) => {
        if (e.jobId !== jobId) return e;
        const r = reconcilePhase(e, "demo", {
          decision,
          reason: decision === "deviation_required" ? (reason || undefined) : undefined,
          notes: decision === "deviation_required" ? notes : undefined,
          decidedBy: pmName,
        });
        if (r.ok === true) {
          toast.success(
            decision === "spec_valid"
              ? "Spec marked valid — next phase unlocked."
              : "Deviation logged — job parked.",
          );
          // Reset form on success.
          setDecision("");
          setReason("");
          setNotes("");
          return r.execution;
        } else {
          toast.error(r.reason);
          return e;
        }
      }),
    );
  };

  const otherTooShort =
    decision === "deviation_required" &&
    reason === "other" &&
    notes.trim().length < OTHER_MIN;

  return (
    <Card
      className={`shadow-none ${
        parkedOnDeviation
          ? "border-destructive/40"
          : "border-warning/40 bg-warning/[0.02]"
      }`}
    >
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2">
            {parkedOnDeviation ? (
              <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
            ) : (
              <ShieldQuestion className="h-4 w-4 text-warning mt-0.5" />
            )}
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.12em] text-muted-foreground">
                Spec Reconciliation Gate
              </p>
              <h3 className="text-sm font-semibold mt-0.5">
                {parkedOnDeviation
                  ? "Job parked — deviation flagged"
                  : "PM review required after demo"}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5 max-w-md">
                Demo passed QA. Confirm the bound JobSpec still matches reality
                before unlocking the next phase. This prevents silent field
                improvisation.
              </p>
            </div>
          </div>
          <Badge variant="outline" className="gap-1">
            <Lock className="h-3 w-3" /> Next phase locked
          </Badge>
        </div>

        {/* Existing deviation record (if parked) */}
        {parkedOnDeviation && demoPhase.reconciliation && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-xs space-y-1">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-destructive">
                Deviation:{" "}
                {DEVIATION_REASONS.find((r) => r.id === demoPhase.reconciliation?.reason)?.label ??
                  demoPhase.reconciliation.reason}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {demoPhase.reconciliation.decidedBy} ·{" "}
                {new Date(demoPhase.reconciliation.decidedAt).toLocaleString()}
              </span>
            </div>
            {demoPhase.reconciliation.notes && (
              <p className="text-foreground/80 whitespace-pre-wrap">
                {demoPhase.reconciliation.notes}
              </p>
            )}
            <p className="text-[10px] text-muted-foreground pt-1">
              Job remains parked. Resolution flow (change order / spec update)
              is not yet implemented — re-reconcile below once the spec is
              confirmed valid.
            </p>
          </div>
        )}

        {/* Decision form */}
        <div className="space-y-3 pt-1 border-t">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">PM Name</Label>
              <Input
                value={pmName}
                onChange={(e) => setPmName(e.target.value)}
                placeholder="Who is reconciling?"
                className="h-8 text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Decision</Label>
              <Select
                value={decision}
                onValueChange={(v) => setDecision(v as ReconciliationDecision)}
              >
                <SelectTrigger className="h-8 text-xs mt-1">
                  <SelectValue placeholder="Select decision…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spec_valid">Spec still valid</SelectItem>
                  <SelectItem value="deviation_required">Spec deviation required</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {decision === "deviation_required" && (
            <>
              <div>
                <Label className="text-xs">Deviation Reason</Label>
                <Select
                  value={reason}
                  onValueChange={(v) => setReason(v as DeviationReason)}
                >
                  <SelectTrigger className="h-8 text-xs mt-1">
                    <SelectValue placeholder="Select reason…" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEVIATION_REASONS.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">
                  Notes
                  {reason === "other" && (
                    <span className="text-muted-foreground ml-1">
                      (min {OTHER_MIN} chars for "Other" — {notes.trim().length}/
                      {OTHER_MIN})
                    </span>
                  )}
                </Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="What's different from the spec? Be specific."
                  className="min-h-[72px] text-xs mt-1"
                />
                {otherTooShort && (
                  <p className="text-[10px] text-destructive mt-1">
                    "Other" requires meaningful detail.
                  </p>
                )}
              </div>
            </>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-[10px] text-muted-foreground max-w-sm">
              {decision === "spec_valid"
                ? "Marking valid will unlock the next phase."
                : decision === "deviation_required"
                ? "Logging a deviation parks the job — next phase stays locked."
                : "Choose a decision to continue."}
            </p>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={
                !decision ||
                !pmName.trim() ||
                (decision === "deviation_required" && (!reason || !notes.trim())) ||
                otherTooShort
              }
            >
              {decision === "spec_valid" ? (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  Mark Spec Valid
                </>
              ) : (
                <>
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  Log Deviation
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default SpecReconciliationGate;
